import { getEnumValueByKey } from '@/Infrastructure/Utilities';
import { StreetTurn } from '@api/Trinium/Endpoints/StreetTurn/types';
import { LegStatus, LegType } from '@api/Trinium/types';
import DateSlot from '@components/DataGrid/DateSlot';
import Tag from '@components/Tag';
import StackedTextCell from '@components/DataGrid/ColumnDefinitions/StackedTextCell';

import styles from './DataGrid.StreetTurn.module.css';
import gStyles from '@components/DataGrid/RenderCell.module.css';

interface StreetTurnMobileItemProps {
	streetTurn: StreetTurn;
}

export default function StreetTurnMobileItem({ streetTurn }: StreetTurnMobileItemProps) {
	const {
		jobId,
		csnNo,
		legNo,
		legType,
		legStatus,
		isLive,
		isHazmat,
		isOverweight,
		isBulk,
		cntrId,
		cntrSize,
		cntrType,
		cntrProviderName,
		drvName,
		tractorId,
		fclDeliveryDate,
		fclLegType,
		fclLegNo,
		scheduledDeliveryDateFrom,
		scheduledDeliveryDateTo,
		puName,
		puCity,
		puState,
		jobCategoryTransportationType
	} = streetTurn;

	const status = getEnumValueByKey(LegStatus, legStatus);

	return (
		<div className={styles['mobile-li']}>
			<div className={styles['flex-row']}>
				<div className={styles['flex-col']}>
					{/* Order */}
					<div>
						<div className={styles.heading}>Order Number</div>
						<div className={styles['order-number']}>
							<span className={gStyles.bold}>{jobId}</span>
							<span className={gStyles['secondary-text']}>/{csnNo.toString().padStart(3, '0')}</span>
						</div>
						<div className={styles['order-info']}>
							<span className={gStyles.bold}>{jobCategoryTransportationType.toUpperCase()}</span>
							<span
								className={gStyles.bold}
								title={`Leg ${String(legNo)} - ${getEnumValueByKey(LegType, legType) ?? ''}`}
							>
								{`${String(legNo)}${legType ? ` - ${legType}` : ''}`}
							</span>
						</div>
					</div>

					{/* Leg Info */}
					<div>
						<div className={styles.heading}>Leg Info</div>
						<Tag
							text={status}
							tagStyle={legStatus}
							align='left'
						/>
						<div className={styles['leg-icon-toolbar']}>
							<Tag
								text='LIVE'
								tagStyle={isLive ? 'live' : 'disabled'}
								dense
							/>
							<Tag
								text='HAZ'
								tagStyle={isHazmat ? 'haz' : 'disabled'}
								dense
							/>
							<Tag
								text='WGT'
								tagStyle={isOverweight ? 'wgt' : 'disabled'}
								dense
							/>
							<Tag
								text='BULK'
								tagStyle={isBulk ? 'bulk' : 'disabled'}
								dense
							/>
						</div>
					</div>

					{/* Container */}
					<div>
						<div className={styles.heading}>Container</div>
						<StackedTextCell
							primaryText={cntrId}
							secondaryText={`${cntrSize} ${cntrType}`}
						/>
						<div className={`${styles['container-provider']} ${styles.ellipsis}`}>{cntrProviderName}</div>
					</div>
				</div>
				<div className={styles['flex-col']}>
					{/* Driver */}
					<div>
						<div className={styles.heading}>Driver / Truck</div>
						<StackedTextCell
							primaryText={drvName ?? '-'}
							primaryClassName={styles.ellipsis}
							secondaryText={tractorId?.toString()}
						/>
					</div>

					{/* Order FCL */}
					<div>
						<div className={styles.heading}>Order FCL</div>
						<StackedTextCell
							primaryText={fclDeliveryDate?.dateTime}
							secondaryText={`${String(fclLegNo)}${fclLegType ? ` - ${fclLegType}` : ''}`}
						/>
					</div>

					{/* Scheduled Delivery */}
					<div>
						<div className={styles.heading}>Scheduled Delivery</div>
						<DateSlot
							fromDate={scheduledDeliveryDateFrom}
							toDate={scheduledDeliveryDateTo}
							usePastDueStyle
						/>
					</div>

					{/* Pickup */}
					<div>
						<div className={styles.heading}>Pickup</div>
						<StackedTextCell
							primaryText={puName}
							primaryClassName={styles.ellipsis}
							secondaryText={`${puCity ?? ''}, ${puState ?? ''}`}
						/>
					</div>
				</div>
			</div>
		</div>
	);
}
